import { Injectable } from '@angular/core';
import { Observable, of, delay, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Question, CustomerAdmin } from '../models/communication.model';

@Injectable({
  providedIn: 'root'
})
export class CommunicationService {
  // Use BehaviorSubject to allow components to subscribe to updates
  private questionsSubject = new BehaviorSubject<Question[]>([]);
  questions$ = this.questionsSubject.asObservable();

  // Add new methods for customer-admin chat
  private customerAdminMessagesSubject = new BehaviorSubject<Question[]>([]);
  customerAdminMessages$ = this.customerAdminMessagesSubject.asObservable();

  private mockCustomerAdmins: CustomerAdmin[] = [
    { id: 'ca1', name: 'Customer Admin Alice' },
    { id: 'ca2', name: 'Customer Admin Bob' }
  ];

  constructor() {
    this.loadInitialMockQuestions();
  }

  private generateNewId(): string {
    return 'id-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
  }

  private loadInitialMockQuestions(): void {
    const mockData: readonly Question[] = [
      {
        id: 'q1',
        text: 'Client is asking about the deadline for phase 1 development. Can we provide an update?',
        authorRole: 'drafter',
        authorName: 'drafter 1',
        status: 'new',
        updatedAt: new Date('2025-05-10T12:00:00Z')
      },
      {
        id: 'q2',
        text: 'There is an issue with the latest build on the staging server. The login page is not rendering correctly. Attached is a screenshot.',
        authorRole: 'drafter',
        authorName: 'drafter 2',
        status: 'new',
        updatedAt: new Date('2025-05-09T12:00:00Z')
      },
      {
        id: 'q3',
        text: 'Requesting clarification on the color palette for the new branding project. The document seems to have conflicting hex codes.',
        authorRole: 'drafter',
        authorName: 'drafter 3',
        status: 'answered',
        adminResponse: 'The correct hex codes are #FF5733 for primary and #33FF57 for secondary. The document has been updated.',
        updatedAt: new Date('2025-05-19T12:00:00Z')
      }
    ] as const;

    // Simulate API call delay
    of([...mockData])
      .pipe(delay(1000))
      .subscribe(data => {
        this.questionsSubject.next(data);
      });
  }

  getQuestions(): Observable<Question[]> {
    return this.questions$;
  }

  getCustomerAdmins(): Observable<CustomerAdmin[]> {
    return of(this.mockCustomerAdmins).pipe(delay(500)); // Simulate API call
  }

  getQuestionById(id: string): Observable<Question | undefined> {
    return this.questions$.pipe(
      map(questions => questions.find(q => q.id === id))
    );
  }

  addAnswerToQuestion(questionId: string, formData: FormData): Observable<Question> {
    const currentQuestions = this.questionsSubject.getValue();
    const questionIndex = currentQuestions.findIndex(q => q.id === questionId);

    if (questionIndex === -1) {
      throw new Error(`Question with id ${questionId} not found`);
    }

    const originalQuestion = currentQuestions[questionIndex];
    const answerText = formData.get('text') as string;
    const imageFile = formData.get('image') as File;

    // In a real implementation, you would upload the image to a server
    // and get back a URL. For this mock implementation, we'll create a data URL
    let photoUrl: string | undefined;
    if (imageFile) {
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);
      photoUrl = URL.createObjectURL(imageFile);
    }

    const updatedQuestion = {
      ...originalQuestion,
      status: 'answered' as Question['status'],
      adminResponse: answerText,
      photo: photoUrl
    };

    // If this question is related to a customer admin chat, add the response there too
    if (originalQuestion.targetCustomerAdmin) {
      const customerAdminMessage: Question = {
        id: this.generateNewId(),
        text: answerText,
        photo: photoUrl,
        authorRole: 'admin',
        authorName: 'Internal Admin',
        status: 'answered',
        isSystemMessage: true,
        relatedToOriginalQuestionId: questionId,
        targetCustomerAdmin: originalQuestion.targetCustomerAdmin,
        updatedAt: new Date()
      };

      const currentMessages = this.customerAdminMessagesSubject.getValue();
      this.customerAdminMessagesSubject.next([customerAdminMessage, ...currentMessages]);
    }

    const updatedQuestions = [
      ...currentQuestions.slice(0, questionIndex),
      updatedQuestion,
      ...currentQuestions.slice(questionIndex + 1)
    ];

    this.questionsSubject.next(updatedQuestions);
    return of(updatedQuestion).pipe(delay(300));
  }

  addClarificationRequest(originalQuestionId: string, clarificationText: string, targetAdmin: CustomerAdmin): Observable<Question> {
    const currentQuestions = this.questionsSubject.getValue();
    const originalQuestionIndex = currentQuestions.findIndex(q => q.id === originalQuestionId);

    if (originalQuestionIndex === -1) {
      throw new Error(`Original question with id ${originalQuestionId} not found`);
    }

    // Update original question's status
    const updatedOriginalQuestion = {
      ...currentQuestions[originalQuestionIndex],
      status: 'pending_customer' as Question['status']
    };

    let updatedQuestions = [
      ...currentQuestions.slice(0, originalQuestionIndex),
      updatedOriginalQuestion,
      ...currentQuestions.slice(originalQuestionIndex + 1)
    ];

    // Create the new system message for clarification
    const systemMessageToCA: Question = {
      id: this.generateNewId(),
      text: clarificationText,
      authorRole: 'admin',
      authorName: 'Internal Admin',
      status: 'new',
      isSystemMessage: true,
      relatedToOriginalQuestionId: originalQuestionId,
      targetCustomerAdmin: { id: targetAdmin.id, name: targetAdmin.name },
      updatedAt: new Date('2025-05-10T12:00:00Z')
    };

    updatedQuestions = [systemMessageToCA, ...updatedQuestions];
    this.questionsSubject.next(updatedQuestions);

    return of(systemMessageToCA).pipe(delay(300));
  }

  updateQuestionStatus(questionId: string, newStatus: Question['status']): Observable<Question> {
    const currentQuestions = this.questionsSubject.getValue();
    const questionIndex = currentQuestions.findIndex(q => q.id === questionId);

    if (questionIndex === -1) {
      throw new Error(`Question with id ${questionId} not found`);
    }

    const updatedQuestion = {
      ...currentQuestions[questionIndex],
      status: newStatus
    };

    const updatedQuestions = [
      ...currentQuestions.slice(0, questionIndex),
      updatedQuestion,
      ...currentQuestions.slice(questionIndex + 1)
    ];

    this.questionsSubject.next(updatedQuestions);
    return of(updatedQuestion).pipe(delay(300)); // Simulate API delay
  }

  getMessagesWithCustomerAdmin(customerAdminId: string): Observable<Question[]> {
    return this.questions$.pipe(
      map(questions => {
        // Get all messages related to this customer admin
        const customerAdminMessages = questions.filter(q =>
          q.targetCustomerAdmin?.id === customerAdminId ||
          (q.authorRole === 'customer-admin' && q.authorName === this.mockCustomerAdmins.find(ca => ca.id === customerAdminId)?.name)
        );

        // Sort by date, newest first
        return customerAdminMessages.sort((a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      })
    );
  }

  sendMessageToCustomerAdmin(customerAdminId: string, formData: FormData, relatedToOriginalQuestionId: string): Observable<Question> {
    const customerAdmin = this.mockCustomerAdmins.find(ca => ca.id === customerAdminId);
    if (!customerAdmin) {
      throw new Error(`Customer admin with id ${customerAdminId} not found`);
    }

    const messageText = formData.get('text') as string;
    const imageFile = formData.get('image') as File;

    // In a real implementation, you would upload the image to a server
    // and get back a URL. For this mock implementation, we'll create a data URL
    let photoUrl: string | undefined;
    if (imageFile) {
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);
      photoUrl = URL.createObjectURL(imageFile);
    }

    // Create a new question for the customer admin chat
    const newQuestion: Question = {
      id: this.generateNewId(),
      text: messageText,
      photo: photoUrl,
      authorRole: 'admin',
      authorName: 'Internal Admin',
      status: 'pending_customer',
      isSystemMessage: true,
      targetCustomerAdmin: { id: customerAdmin.id, name: customerAdmin.name },
      relatedToOriginalQuestionId,
      updatedAt: new Date()
    };

    // Add to the main questions list only
    const currentQuestions = this.questionsSubject.getValue();
    this.questionsSubject.next([newQuestion, ...currentQuestions]);

    return of(newQuestion).pipe(delay(300));
  }
}
